/**
 * Related Meetings Component
 * Shows AI-powered related meetings based on vector similarity search
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { 
  Link2, Calendar, Clock, Loader2, RefreshCw, Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RelatedMeetings = ({ meetingId, token }) => {
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchRelated = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.get(
        `${API}/api/meeting-intelligence/meetings/${meetingId}/related?token=${token}&limit=5`
      );
      
      setRelated(res.data.related_meetings || []);
    } catch (err) {
      console.error('Failed to fetch related meetings:', err);
      setError('Could not load related meetings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (meetingId && token) {
      fetchRelated();
    }
  }, [meetingId, token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-blue-600';
    if (score >= 0.4) return 'text-amber-600';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <Card data-testid="related-meetings-loading">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-purple-500" />
            Related Meetings
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="related-meetings-error">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-purple-500" />
            Related Meetings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="ghost" size="sm" onClick={fetchRelated} className="mt-2">
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (related.length === 0) {
    return (
      <Card data-testid="related-meetings-empty">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-purple-500" />
            Related Meetings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No related meetings found. Index more meetings to enable AI recommendations.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="related-meetings">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Sparkles className="h-4 w-4 text-purple-500" />
          Related Meetings
          <Badge variant="secondary" className="ml-auto text-xs">
            AI Powered
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {related.map((meeting, idx) => (
          <div
            key={meeting.meeting_id || idx}
            onClick={() => navigate(`/meeting-intelligence/meetings/${meeting.meeting_id}`)}
            className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer group"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/20">
                <Link2 className="h-4 w-4 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                  {meeting.title}
                </p>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  {meeting.scheduled_start && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(meeting.scheduled_start)}
                    </span>
                  )}
                  <span className={`flex items-center gap-1 ${getScoreColor(meeting.similarity_score)}`}>
                    <Sparkles className="h-3 w-3" />
                    {Math.round(meeting.similarity_score * 100)}% match
                  </span>
                </div>
                {meeting.matched_content && (
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                    {meeting.matched_content}
                  </p>
                )}
              </div>
              <Badge variant={meeting.status === 'completed' ? 'default' : 'outline'} className="text-xs">
                {meeting.status}
              </Badge>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default RelatedMeetings;
